// src/pages/locations/useLocationScope.ts
import React from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Country / region context for the Locations screens, held in the URL.
 *
 * Keeping it in the query string (instead of component state) means the
 * selection survives navigating into a create/edit page and back, and a
 * copied link opens on the same slice of data.
 */
export type LocationScope = {
  country: string;
  /** "" = all regions, "null" = cities with no region, otherwise a region id */
  region: string;
};

export function useLocationScope() {
  const [searchParams, setSearchParams] = useSearchParams();

  const country = (searchParams.get("country") ?? "").trim().toUpperCase();
  const region = (searchParams.get("region") ?? "").trim();
  const search = searchParams.get("q") ?? "";

  const setSearch = React.useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) params.set("q", next);
          else params.delete("q");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setCountry = React.useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const value = String(next ?? "").trim().toUpperCase();

          if (value) params.set("country", value);
          else params.delete("country");

          // A region only makes sense within its country.
          params.delete("region");

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setRegion = React.useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const value = String(next ?? "").trim();

          if (value) params.set("region", value);
          else params.delete("region");

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const scope: LocationScope = React.useMemo(
    () => ({ country, region }),
    [country, region]
  );

  return { country, region, search, scope, setCountry, setRegion, setSearch };
}

/** Region query-string value → the regionId filter the API expects. */
export function toRegionId(region: string): number | null | undefined {
  if (!region) return undefined; // all regions
  if (region === "null") return null; // cities with no region
  const n = Number(region);
  return Number.isFinite(n) ? n : undefined;
}
